import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './App.module.scss';
import { socket } from './socket';
import { CardContainer } from './card/CardContainer';
import { StartModal, EndModal, IMultiplayerData } from './modal/Modal';
import { Timer } from './buttons/timer/Timer';
import { ImageType } from './icon/Icon';
import { MuteButton } from './buttons/MuteButton';
import tadaAudio from './assets/sounds/tada.wav';
import { OpponentCardContainer } from './card/OpponentCards';

// TODO: Fix timer in multiplayer mode
// TODO: Move the timer and mute icons to the left or right side of the cards
// TODO: Fix card flickering - right now I have the card move up
// with css animation and then once it's moved, the 
// icons are replaced and the card moves back down
// maybe I can somehow get it so that I can just move the card and leave it there

export type ModalSelection = 'start' | 'waitingroom' | 'singleplayer' | 'multiplayer' | 'countdown';

export interface IGameData {
  roomCode: string;
  player: IMultiplayerData;
}

function App() {
  const [showStartModal, setShowStartModal] = useState(true);
  const [showEndModal, setShowEndModal] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [showPenaltyDelay, setShowPenaltyDelay] = useState(false);
  const [penaltyTime, setPenaltyTime] = useState(0);
  const [time, setTime] = useState(0);
  const [isPaused, setIsPaused] = useState(true);
  const [isDisabled, setDisabled] = useState(false);
  const [images, setImages] = useState<ImageType[]>([]);
  const [isMultiplayer, setIsMultiplayer] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [winner, setWinner] = useState<IMultiplayerData>();
  const [isMuted, setIsMuted] = useState(false);
  const [error, setError] = useState<string>();
  const [modalSelection, setModalSelection] = useState<ModalSelection>('start');
  
  // server data
  const [isConnected, setIsConnected] = useState(socket.connected);
  const [players, setPlayers] = useState<string[]>([]);
  const [roomCode, setRoomCode] = useState<string>();
  const [gameData, setGameData] = useState<any>();

  const timeAsString = useMemo(
    () => {
      const minutes = Math.floor((time % 360000) / 6000);
      const seconds = Math.floor((time % 6000) / 100);
      return minutes.toString().padStart(2, "0") + ':' + seconds.toString().padStart(2, "0");
    }, 
    [time]
  );

  const onTimerUpdate = useCallback((time: number) => {
    setTime(time);
  }, []);

  // Penalty timer
  useEffect(() => {
    // Sets time from 0 to 1 every 10 miliseconds using setInterval
    let intervalId: NodeJS.Timer | undefined = undefined;

    if (showPenaltyDelay) {
      intervalId = setInterval(() => setPenaltyTime(penaltyTime + 1), 10);
      if (Math.floor((penaltyTime % 6000) / 100) === 5) {
        clearInterval(intervalId);
        setShowPenaltyDelay(false);
        setPenaltyTime(0);
        setDisabled(false);
      }
    }
    
    return () => clearInterval(intervalId);
  }, [penaltyTime, showPenaltyDelay]);

  const onClickWrongIcon = useCallback(() => {
    setShowPenaltyDelay(true);
    setDisabled(true);
  }, []);

  const onCloseStartModal = useCallback(() => {
    setIsPaused(false);
    setShowStartModal(false);
  }, []);

  const onCloseEndModal = useCallback(() => {
    setShowEndModal(false);
  }, []);

  const onAllDone = useCallback((winner?: IMultiplayerData) => {
    const allDoneSound = new Audio(tadaAudio);
    allDoneSound.play();
    
    setIsPaused(true);
    setShowEndModal(true);
    setWinner(winner);
  }, []);

  const onClickSinglePlayer = useCallback(() => {
    setModalSelection('singleplayer');
  }, []);

  const onClickMultiplayer = useCallback(() => {
    setModalSelection('multiplayer');
    setIsMultiplayer(true);
  }, []);

  const onClickBackButton = useCallback(() => {
    if (modalSelection === 'multiplayer') {
      setModalSelection('start');
      setIsMultiplayer(false);
    } 
    setError(undefined);
  }, [modalSelection]);

  const onPlayerJoined = useCallback((username: string, roomCode: string) => {
    setRoomCode(roomCode);
    // take to waiting room
    setModalSelection('waitingroom');
  }, []);

  // Socket code

  useEffect(() => {
    function onConnect() {
      setIsConnected(true);
    }

    function onSession(sessionId: string, userId: string) {
      console.log("received session", sessionId);
      // attach the session ID to the next reconnection attempts
      const data = { sessionId: sessionId, userId: userId };
      socket.auth = { data };
      // store it in the localStorage
      sessionStorage.setItem("sessionId", sessionId);
    }

    function onDisconnect() {
      setIsConnected(false);
    }

    function onPlayersInGame(players: string[]) {
      setPlayers(players);
    }

    function onCreatedGame(username: string, game: string) {
      setRoomCode(game);
      // take to waiting room
      setModalSelection('waitingroom');
    }

    function onJoinGameError(joinGameError: string) {
      setError(joinGameError);
    }

    function onStartGame(gameData: any) {
      setGameData(gameData);
      setShowStartModal(false);
      setShowCountdown(false);
    }

    function onImages(images: ImageType[]) {
      console.log("setting images");
      setImages(images);
    }

    function onUpdateGame(gameData: any) {
      setGameData(gameData);
      console.log("Game updated. gameData: ", gameData);
    }

    function onCountdown(time: number) {
      if (time === 3) {
        setShowCountdown(true);
      }
      setCountdown(time);
    }

    socket.on('session', onSession);
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('playersInGame', onPlayersInGame);
    socket.on('createdGame', onCreatedGame);
    socket.on('startGame', onStartGame);
    socket.on('playerJoined', onPlayerJoined);
    socket.on('images', onImages);
    socket.on('updateGame', onUpdateGame);
    socket.on('countdown', onCountdown);
    socket.on('gameOver', onAllDone);
    socket.on('joinGameError', onJoinGameError);

    return () => {
      socket.off('session', onSession);
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('playersInGame', onPlayersInGame);
      socket.off('createdGame', onCreatedGame);
      socket.off('playerJoined', onPlayerJoined);
      socket.off('images', onImages);
      socket.off('updateGame', onUpdateGame);
      socket.off('countdown', onCountdown);
      socket.off('gameOver', onAllDone);
      socket.off('joinGameError', onJoinGameError);
    };
  }, [onAllDone, onPlayerJoined]);

  useEffect(() => {
    console.log('isConnected: ', isConnected);
  }, [isConnected]);

  const onStart = useCallback(() => {
    // Reset error
    setError(undefined);

    socket.emit('startGame', roomCode?.toLocaleUpperCase());
  }, [roomCode]);

  const onClickSharedNumber = useCallback((sharedNumber: number) => {
    const gameData = {
      room: roomCode,
      action: sharedNumber
    };

    socket.emit('playerAction', gameData);
  }, [roomCode]);

  // End socket code

  const penaltyDelay = useMemo(() => {
    if (showPenaltyDelay) {
      return <div className={styles.penaltyDelay}>
        <div className={styles.penaltyDelayText}>
          Wrong buddy - 5 second penalty
        </div>
      </div>;
    }
    return null;
  }, [showPenaltyDelay]);

  return (
    <div className={styles.App}>
      {
        showStartModal && !showCountdown ? 
          <StartModal 
            onClose={onCloseStartModal} 
            players={players} 
            roomCode={roomCode} 
            onStartGame={onStart}
            countdown={countdown}
            onSelectMultiplayer={onClickMultiplayer}
            onSelectSinglePlayer={onClickSinglePlayer}
            error={error}
            selection={modalSelection}
            onClickBackButton={onClickBackButton}
            onJoinGame={onPlayerJoined}
          /> :
          showCountdown ? 
          <div className={styles.countdown}>{countdown !== 0 ? countdown : 'Go!'}</div> :
          showEndModal ?
          <EndModal onClose={onCloseEndModal} time={timeAsString} winner={winner} /> :
        (
          <div className={styles.multiplayerContainerRoot}>
            {isMultiplayer && <OpponentCardContainer opponents={gameData.opponents} lastCorrectPlayer={gameData.lastCorrectPlayer} />}
            <div className={styles.multiplayerContainer}>
              <CardContainer 
                onAllDone={onAllDone} 
                onClickWrongIcon={onClickWrongIcon} 
                isDisabled={isDisabled}
                onClickSharedNumber={onClickSharedNumber}
                isMuted={isMuted}
                icons={images}
                gameData={gameData}
                isMultiplayer={isMultiplayer}
              />
              {penaltyDelay}
              <div className={styles.buttonsContainer} id='buttonsContainer'>
                {!isMultiplayer && <Timer onTimerUpdate={onTimerUpdate} isPaused={isPaused} />}
                <MuteButton 
                  isMuted={isMuted}
                  onClick={
                    () => {
                      setIsMuted(isMuted => !isMuted)
                    }
                  } 
                />
              </div>
            </div>
          </div>
        )
      }
    </div>
  );
}

export default App;
